import { inject, InjectionToken, PLATFORM_ID } from '@angular/core';
import { IDataLayer } from '@libs/models/models';
import { MockSessionStorage } from '@libs/shared/injections/mock-session-storage';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '@libs/environment';
import { SendEventHelper } from '@libs/helpers/send-event.helper';
import { Shield, ShieldProps } from 'shield-js-npm';
import { EnvironmentType } from '@libs/models/environment.type';

export const WINDOW = new InjectionToken<Window>('window', {
  providedIn: 'root',
  factory: () => window,
});

export const NAVIGATOR = new InjectionToken<Navigator>('navigator', {
  providedIn: 'root',
  factory: () => inject(WINDOW)?.navigator,
});

export const LOCATION = new InjectionToken<Location | null>('LocationToken', {
  providedIn: 'root',
  factory: () => (typeof window !== 'undefined' ? window.location : null),
});

export const SESSION_STORAGE = new InjectionToken<Storage | null>('SessionStorageToken', {
  providedIn: 'root',
  factory: () => {
    const platform = inject(PLATFORM_ID);
    if (isPlatformBrowser(platform)) {
      return window.sessionStorage;
    } else {
      return new MockSessionStorage();
    }
  },
});

export const LOCAL_STORAGE = new InjectionToken<Storage | null>('LocalStorageToken', {
  providedIn: 'root',
  factory: () => {
    const platform = inject(PLATFORM_ID);
    if (isPlatformBrowser(platform)) {
      return window.localStorage;
    } else {
      return new MockSessionStorage();
    }
  },
});

export const DATA_LAYER = new InjectionToken<Array<IDataLayer>>('DataLayerToken', {
  providedIn: 'root',
  factory: () => (typeof window !== 'undefined' ? (window['dataLayer'] = window['dataLayer'] || []) : []),
});

export const ENVIRONMENT = new InjectionToken<EnvironmentType>('environment', {
  providedIn: 'root',
  factory: () => environment as unknown as EnvironmentType,
});

export const IS_MOBILE = new InjectionToken<boolean>('IsMobile', {
  providedIn: 'root',
  factory: () => !!navigator.userAgent.match(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i),
});

export const SHIELD_TOKEN = new InjectionToken<Promise<Shield | null>>('SHIELD_TOKEN', {
  providedIn: 'root',
  factory: async (): Promise<Shield> => {
    const eventHelper = inject(SendEventHelper);
    if (!environment['shieldSiteId']) {
      return null;
    }
    const shield = new Shield();
    const props: ShieldProps = { siteId: environment['shieldSiteId'] };
    await shield.init(props);
    return shield;
  },
});
